import {
  DashboardIcon,
  DeputateIcon,
  LockIcon,
  ProfileIcon,
  SettingsIcon,
  SidebarContentProps,
  SidebarItemProps,
  SubstitutionsIcon,
  TimetableIcon,
  UpdateIcon,
} from '@bp/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventJobRunning } from './useEventJobRunning';
import { usePermissionChecker } from './usePermissionChecker';

/**
 * Navigationseinträge für Sidebar (Desktop) und Vollbild-Menü (Mobil).
 * Links sind relativ zur Wurzel, Unterpunkte relativ zum Hauptpunkt (`${link}/${sub.link}`).
 */
export const useNavigationItems = () => {
  const { t } = useTranslation();
  const permissions = usePermissionChecker();
  const isEventJobRunning = useEventJobRunning();

  const mainItems = useMemo<SidebarContentProps[]>(() => {
    const planPhase = { title: t('routes.plan.title'), link: t('routes.plan.slug') };
    const phases = [
      { title: t('routes.setup.title'), link: t('routes.setup.slug') },
      planPhase,
      { title: t('routes.evaluate.title'), link: t('routes.evaluate.slug') },
    ];

    return [
      {
        items: [
          {
            icon: <DashboardIcon />,
            link: '',
            title: 'Dashboard',
          },
          ...(permissions?.canReadDeputate()
            ? [
                {
                  icon: <DeputateIcon />,
                  link: t('routes.deputate.slug'),
                  title: t('routes.deputate.title'),
                  sublist: phases,
                },
              ]
            : []),
          ...(permissions?.canReadTimetable()
            ? [
                {
                  icon: <TimetableIcon />,
                  link: t('routes.timetable.slug'),
                  title: t('timetable.title', { count: 2 }),
                  sublist: phases,
                },
              ]
            : []),
          ...(permissions?.canManageSubstitution()
            ? [
                {
                  icon: isEventJobRunning ? <UpdateIcon className={'svg-icon spinning'} /> : <SubstitutionsIcon />,
                  link: t('routes.substitutions.slug'),
                  title: t('substitutions.title', { count: 2 }),
                  // Vertretungen Manage sieht nur „Planen“; Einrichten und Auswerten nur Vertretungen Admin.
                  sublist: permissions?.canWriteSubstitution() ? phases : phases.filter((phase) => phase === planPhase),
                },
              ]
            : []),
        ],
      },
    ];
  }, [permissions, t, isEventJobRunning]);

  const footerItems = useMemo<SidebarItemProps[]>(
    () =>
      [
        ...(permissions?.canManageSettings()
          ? [
              {
                icon: <SettingsIcon />,
                link: t('routes.settings.slug'),
                title: t('settings.title', { count: 2 }),
              },
            ]
          : []),
        ...(permissions?.canManageAdministration()
          ? [
              {
                icon: <LockIcon />,
                link: t('routes.admin.slug'),
                title: t('routes.admin.title'),
              },
            ]
          : []),
        {
          icon: <ProfileIcon />,
          link: t('routes.profile.slug'),
          title: t('profile.title', { count: 1 }),
        },
      ] as SidebarItemProps[],
    [permissions, t],
  );

  return { mainItems, footerItems };
};
